import {
  CostPriceUploadBody,
  CreateCostPricePayload,
  ServiceCostPrices,
  ServiceIdentifier,
  TourService,
} from '~/types/tours/costPrice';

import { json_headers, request } from './common';
import { OkResponse, stripTourPrefix, toursBasePath } from './toursApi';

/**
 * Lists the supplier services on a tour. Cost prices hang off a service, so
 * this is how the supplier and service ids are resolved for a given tour.
 */
export async function getTourServices(tourId: string, options?: { signal?: AbortSignal }): Promise<Array<TourService>> {
  const response: OkResponse<Array<TourService>> = await request(
    `${toursBasePath()}/${stripTourPrefix(tourId)}/services`,
    {
      method: 'GET',
      signal: options?.signal,
    },
  );

  return response.result;
}

function costPricesPath({ supplierId, serviceId }: ServiceIdentifier): string {
  return `${toursBasePath()}/suppliers/${supplierId}/services/${serviceId}/cost-prices`;
}

/** Returns everything the service is priced by, grouped by kind. */
export async function getServiceCostPrices(
  service: ServiceIdentifier,
  options?: { signal?: AbortSignal },
): Promise<ServiceCostPrices> {
  const response: OkResponse<ServiceCostPrices> = await request(costPricesPath(service), {
    method: 'GET',
    signal: options?.signal,
  });

  return response.result;
}

export async function uploadCostPrices(service: ServiceIdentifier, body: CostPriceUploadBody): Promise<void> {
  await request(costPricesPath(service), {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

/**
 * Creates a cost price against one package configuration.
 *
 * Fixed prices go through here rather than the bulk upload: the upload endpoint
 * only writes sliding scales, and its Fixed branch silently stores nothing.
 */
export async function createCostPrice(
  service: ServiceIdentifier,
  { packageConfigurationId, ...body }: CreateCostPricePayload,
): Promise<void> {
  const { supplierId, serviceId } = service;

  await request(
    `${toursBasePath()}/suppliers/${supplierId}/services/${serviceId}/configuration/${packageConfigurationId}/cost-prices`,
    { method: 'POST', headers: json_headers, body: JSON.stringify(body) },
  );
}
